import { useState } from 'react'
import { Minus, Plus, Pencil } from 'lucide-react'
import { formatCurrency } from '@utils'
import { useToast } from '@/hooks/useToast'
import { useSessionStore } from '@/stores'
import { useCartStore } from '@/stores/useCartStore'
import OmpButton from '../components/OmpButton'
import { resolveProductName } from '../utils/omp'
import OmpProductImage from '../components/OmpProductImage'

interface AddProductDrawerProps {
  product?: any
  deliveryLine?: string
  onClose: () => void
  onEditAddress?: () => void
}

const INTERVALS = [
  { value: '30', label: 'Every 30 Days' },
  { value: '60', label: 'Every 60 Days' },
  { value: '90', label: 'Every 90 Days' },
]

const AddProductDrawer = ({ product, deliveryLine, onClose, onEditAddress }: AddProductDrawerProps) => {
  const toast = useToast()
  const sessionId = useSessionStore(s => (s.session as any)?.id)
  const addItemsToSessionCart = useCartStore(s => s.addItemsToSessionCart)
  const [quantity, setQuantity] = useState(1)
  const [interval, setInterval] = useState('30')
  const [pending, setPending] = useState(false)

  if (!product) {
    return null
  }

  const price = product.price ?? product.current_price ?? 0
  const compareAt = product.compare_at_price ?? product.original_price
  const oncePrice = product.one_time_price ?? compareAt ?? price

  // POST /sessions/{id}/cart/ — buy_now_clicked distinguishes one-time vs subscribe.
  const handleAdd = async (recurring: boolean) => {
    if (!sessionId) {
      return
    }
    setPending(true)
    try {
      await addItemsToSessionCart(sessionId, [
        {
          campaign_product: product,
          variant: product.variant ?? null,
          quantity,
          buy_now_clicked: !recurring,
        } as any,
      ])
      toast.success(recurring ? 'Added to your subscription' : 'Added as a one-time purchase')
      onClose()
    } catch {
      toast.error('Could not add the product')
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="omp-action-drawer omp-addproduct">
      <div className="omp-addproduct-img">
        <OmpProductImage source={product} />
      </div>

      <h3 className="omp-addproduct-name">{resolveProductName(product)}</h3>
      <p className="omp-addproduct-price">
        <span className="omp-addproduct-now">{formatCurrency(price)}</span>
        {typeof compareAt === 'number' && compareAt > price && (
          <span className="omp-addproduct-was">{formatCurrency(compareAt)}</span>
        )}
      </p>

      {product.description && <p className="omp-addproduct-desc">{product.description}</p>}

      {deliveryLine && (
        <div className="omp-addproduct-deliver">
          <span className="omp-link-underline">Deliver to {deliveryLine}</span>
          {onEditAddress && (
            <button type="button" className="omp-addproduct-edit" onClick={onEditAddress}>
              <Pencil size={14} /> Edit
            </button>
          )}
        </div>
      )}

      <label className="omp-field-label">Deliver in</label>
      <select
        className="form-select omp-addproduct-select"
        value={interval}
        onChange={e => setInterval(e.target.value)}>
        {INTERVALS.map(i => (
          <option key={i.value} value={i.value}>
            {i.label}
          </option>
        ))}
      </select>

      <label className="omp-field-label">Quantity</label>
      <div className="omp-qty">
        <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))} aria-label="Decrease">
          <Minus size={18} />
        </button>
        <span className="omp-qty-value">{quantity}</span>
        <button type="button" onClick={() => setQuantity(q => q + 1)} aria-label="Increase">
          <Plus size={18} />
        </button>
      </div>

      <div className="omp-action-footer">
        <OmpButton variant="primary" fullWidth disabled={pending} onClick={() => handleAdd(true)}>
          Subscribe for only {formatCurrency(price * quantity)}
        </OmpButton>
        <button type="button" className="omp-link-underline" disabled={pending} onClick={() => handleAdd(false)}>
          Buy once for {formatCurrency(oncePrice * quantity)}
        </button>
      </div>
    </div>
  )
}

export default AddProductDrawer
